import type { ApiKey } from '../services/api';

/**
 * Whose keys may be shown or sent. While viewing as another organization that
 * is the impersonated org, never the caller's own acting org. No id means no
 * key: a brand-only view, or the acting org not resolved yet.
 */
export function effectiveApiKeyOrganizationId(
  isImpersonating: boolean,
  impersonatedOrganizationId: string | null | undefined,
  actingOrganizationId: string | null | undefined,
): string | null {
  const raw = isImpersonating ? impersonatedOrganizationId : actingOrganizationId;
  if (raw == null || raw === '') return null;
  return String(raw);
}

const isUsable = (key: ApiKey): boolean => Boolean(key.key) && key.isActive !== false;

const sameOrganization = (
  keyOrganizationId: ApiKey['organizationId'],
  effectiveOrganizationId: string,
): boolean => keyOrganizationId != null && keyOrganizationId !== '' && String(keyOrganizationId) === effectiveOrganizationId;

/**
 * `GET /api-keys` lists every key for the signed-in user, across organizations.
 * Keep only the effective org's. No effective org, or a key with no
 * organizationId, keeps nothing — never a key from another organization.
 */
export function apiKeysForOrganization(
  keys: readonly ApiKey[],
  organizationId: string | null | undefined,
): ApiKey[] {
  if (organizationId == null || organizationId === '') return [];
  const wanted = String(organizationId);
  return keys.filter((key) => sameOrganization(key.organizationId, wanted));
}

/** Remembered secret first, then the first usable key. Both from `keys` only. */
export function chooseUsableApiKey(
  keys: readonly ApiKey[],
  candidates: readonly (string | null | undefined)[],
): string | null {
  const remembered = candidates.find((candidate) =>
    Boolean(candidate && keys.some((key) => key.key === candidate && isUsable(key))),
  );
  return remembered ?? keys.find(isUsable)?.key ?? null;
}

export function selectApiKeyForOrganization(
  keys: readonly ApiKey[],
  effectiveOrganizationId: string | null | undefined,
  candidates: readonly (string | null | undefined)[],
): { keys: ApiKey[]; chosen: string | null } {
  const scoped = apiKeysForOrganization(keys, effectiveOrganizationId);
  return { keys: scoped, chosen: chooseUsableApiKey(scoped, candidates) };
}
